import { intakeHref } from '../lib/referral';
import React from 'react';
import { ArrowRight, CalendarDays, User, BarChart3, FileText, Stethoscope, ShieldCheck, Truck } from 'lucide-react';
import { products } from '../mock';

const FEATURE_ICONS = [CalendarDays, null, FileText];
const TRUST = [
  { Icon: Stethoscope, title: 'Clinically Guided', body: 'Evaluated by licensed U.S. providers.' },
  { Icon: ShieldCheck, title: 'Personalized Treatment', body: 'Based on your health history and goals.' },
  { Icon: Truck, title: 'Discreet & Convenient', body: 'Delivered directly to your door.' },
];

const IconDot = ({ Icon, size = 'h-10 w-10' }) => (
  <span className={`${size} shrink-0 rounded-full bg-[color:var(--alfa-sand)] flex items-center justify-center`}>
    <Icon size={17} strokeWidth={1.5} className="text-[color:var(--alfa-ink)]" />
  </span>
);

const ProductCard = ({ p, i }) => {
  const slug = p.name.toLowerCase();
  return (
    <article data-testid={`product-card-${slug}`} className="rounded-lg overflow-hidden bg-[#fdfaf6] shadow-[0_20px_50px_-24px_rgba(60,40,20,0.35)] flex flex-col">
      <div className="relative aspect-[16/9] overflow-hidden">
        <img src={p.img} alt={`ALFÀ ${p.name}`} data-testid={`product-img-${slug}`} className="w-full h-full object-cover" />
        <span className="absolute top-4 left-4 bg-white/90 text-[11px] font-medium uppercase tracking-[0.22em] px-3 py-1.5 rounded-md text-[color:var(--alfa-ink)]">{p.tag}</span>
      </div>
      <div className="px-6 pt-5 pb-6 flex flex-col flex-1">
        <div className="flex items-baseline justify-between gap-4">
          <h3 className="text-[26px] tracking-[-0.02em] text-[color:var(--alfa-ink)]">{p.name}</h3>
          <p className="shrink-0" data-testid={`product-price-${slug}`}>
            <span className="text-[26px] font-medium text-[color:var(--alfa-ink)]">{p.price}</span>
            <span className="ml-1 text-[13px] text-[color:var(--alfa-muted)]">{p.unit}</span>
          </p>
        </div>
        <p className="mt-1 sm:text-right text-[12.5px] font-medium text-[color:var(--alfa-ink)]" data-testid={`product-flat-note-${slug}`}>Same monthly price across eligible prescribed doses.</p>
        <p className="mt-2 text-[14.5px] leading-[1.55] text-[color:var(--alfa-muted)]">{p.short}</p>
        <div className="mt-auto pt-5 grid grid-cols-3 divide-x divide-neutral-900/10">
          {p.chips.map((c, j) => (
            <div key={c} className={`flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-2.5 ${j ? 'pl-2.5 sm:pl-3' : ''} pr-1.5 sm:pr-2`}>
              <IconDot Icon={FEATURE_ICONS[j] || (i ? BarChart3 : User)} />
              <span className="text-[12px] sm:text-[12.5px] leading-[1.35] text-[color:var(--alfa-ink)]">{c}</span>
            </div>
          ))}
        </div>
        <a href={intakeHref()} target="_blank" rel="noopener noreferrer" data-testid={`product-qualify-btn-${slug}`} className="btn-primary mt-5 w-full flex items-center justify-center gap-2.5 rounded-md py-3.5 text-[15.5px]">
          See if you qualify <ArrowRight size={17} />
        </a>
      </div>
    </article>
  );
};

export default function Arsenal() {
  return (
    <section id="arsenal" data-testid="arsenal-section" className="scroll-mt-20 px-4 sm:px-6 py-10 sm:py-12 bg-[radial-gradient(ellipse_at_top,#fbf6ef_0%,#efe5d8_70%,#e8dccd_100%)]">
      <div className="max-w-[1100px] mx-auto">
        <div className="text-center">
          <span className="inline-flex uppercase text-[11px] tracking-[0.26em] font-medium text-[color:var(--alfa-ink)] px-4 py-2 rounded-full bg-white/90">Weight loss treatments</span>
          <h2 className="mt-4 text-[34px] sm:text-[44px] lg:text-[50px] leading-[1.05] tracking-[-0.03em] text-[color:var(--alfa-ink)]">
            Personalized <span className="text-[color:var(--alfa-royal)]">GLP-1 Care</span>
          </h2>
          <p className="mt-3 mx-auto max-w-[640px] text-[15px] leading-[1.55] text-[color:var(--alfa-ink)]/80">
            Access GLP-1 treatment through licensed medical providers who evaluate your health history and determine whether treatment is medically appropriate.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
          {products.map((p, i) => <ProductCard key={p.name} p={p} i={i} />)}
        </div>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-0 sm:divide-x divide-neutral-900/10">
          {TRUST.map(({ Icon, title, body }, i) => (
            <div key={title} data-testid={`arsenal-trust-${i + 1}`} className="flex items-center gap-4 sm:justify-center sm:px-4">
              <IconDot Icon={Icon} size="h-12 w-12" />
              <div>
                <p className="text-[14.5px] font-medium text-[color:var(--alfa-ink)]">{title}</p>
                <p className="text-[13px] text-[color:var(--alfa-muted)]">{body}</p>
              </div>
            </div>
          ))}
        </div>

        <p data-testid="compounded-disclosure" className="mt-7 mx-auto max-w-[820px] text-center text-[11.5px] leading-[1.6] italic text-[color:var(--alfa-ink)]/75">
          Compounded medications are not FDA-approved. The FDA does not review compounded medications for safety, effectiveness, or quality before they are marketed. Treatment eligibility and medication selection are determined by a licensed healthcare provider. Individual results vary.
        </p>
      </div>
    </section>
  );
}
